import { AxiosPromise } from 'axios';
import { INTERNAL_PHONENUMBERS, URLS } from '@/services/Constants';
import SorterService from '@/services/SorterService';
import { commonAxios as axios, cloudAxios } from '@/services/Authentication';

export default {
    // GET

    /**
     * Return a list of available PhoneNumbers
     *
     * @returns {AxiosPromise<any>}
     */
    getAllPhonenumbers(): AxiosPromise<any> {
        return axios().request({
            url: URLS().urlPhonenumbers,
            method: 'get'
        });
    },
    /**
     * Return the PhoneNumber with the given {phoneNumberId}
     *
     * @param {string} phonenumberId
     * @returns {AxiosPromise<any>}
     */
    getPhonenumber(phonenumberId: string): AxiosPromise<any> {
        return axios().request({
            url: URLS().urlPhonenumbers + '/' + phonenumberId,
            method: 'get'
        });
    },

    // POST
    orderPhonenumbers(areaCode: number, type: number): AxiosPromise<any> {
        return cloudAxios().request({
            url: URLS().cloudUrlNumbers + '/?area_code=' + areaCode + '&number_type=' + type,
            method: 'post',
            data: {}
        });
    },

    // POSTPROCESSING

    /**
     * Return formatted and sorted internal/external numbers
     *
     * @param {any[]} numbers
     * @param {*} internalPhonenumberId
     * @returns {{ internal: any[]; external: any[]; internalPhoneNumberId: any }}
     */
    splitAndSortNumbers(
        numbers: any[],
        internalPhonenumberId: any
    ): { internal: any[]; external: any[]; internalPhoneNumberId: any } {
        let internalPhoneId = internalPhonenumberId;
        const result = numbers.reduce(
            (acc: { internal: []; external: [] }, entry: any) => {
                if (this.entryPassesClientDefinedFilter(entry)) {
                    return {
                        ...acc,
                        external: [
                            ...acc.external,
                            this._createExternalPhonenumber(entry),
                        ]
                    };
                } else if (entry.type === 'INTERNAL') {
                    if (
                        internalPhoneId === null &&
                        entry.assignedGroupAccountId === null &&
                        entry.assignedUserAccountId === null &&
                        Number(entry.number) >=
                            INTERNAL_PHONENUMBERS.SmallestInternalNumber &&
                        Number(entry.number) <=
                            INTERNAL_PHONENUMBERS.LargestInternalNumber
                    ) {
                        internalPhoneId = entry.id;
                    }
                    return {
                        ...acc,
                        internal: [
                            ...acc.internal,
                            this._createInternalPhonenumber(entry),
                        ]
                    };
                } else {
                    return acc;
                }
            },
            {
                external: [],
                internal: []
            }
        );

        return {
            external: SorterService._sortArrayOfStringsByParam(
                result.external,
                'phoneNumberComplete'
            ),
            internal: SorterService._sortArrayOfStringsByParam(
                result.internal,
                'number'
            ),
            internalPhoneNumberId: internalPhoneId
        };
    },

    /**
     * Return formatted and sorted assignable internal/external numbers for groups
     *
     * @param {any[]} numbers
     * @param {*} internalPhonenumberId
     * @returns {{ internal: any[]; external: any[]; internalPhoneNumberId: any }}
     */
    splitAndSortNumbersForGroups(
        numbers: any[],
        internalPhonenumberId: any
    ): { internal: any[]; external: any[]; internalPhoneNumberId: any } {
        let internalPhoneId = internalPhonenumberId;
        const result = numbers.reduce(
            (acc: { internal: []; external: [] }, entry: any) => {
                if (this.entryPassesClientDefinedFilter(entry)) {
                    return {
                        ...acc,
                        external: [
                            ...acc.external,
                            this._createExternalPhonenumberForGroup(entry),
                        ]
                    };
                } else if (entry.type === 'INTERNAL') {
                    if (
                        internalPhoneId === null &&
                        entry.assignedGroupAccountId === null &&
                        entry.assignedUserAccountId === null &&
                        entry.number >=
                            INTERNAL_PHONENUMBERS.SmallestInternalNumber &&
                        entry.number <=
                            INTERNAL_PHONENUMBERS.LargestInternalNumber
                    ) {
                        internalPhoneId = entry.id;
                    }
                    return {
                        ...acc,
                        internal: [
                            ...acc.internal,
                            this._createInternalPhonenumberForGroup(entry),
                        ]
                    };
                } else {
                    return acc;
                }
            },
            {
                external: [],
                internal: []
            }
        );

        return {
            external: SorterService._sortArrayOfStringsByParam(
                result.external,
                'phoneNumberComplete'
            ),
            internal: SorterService._sortArrayOfStringsByParam(
                result.internal,
                'extension'
            ),
            internalPhoneNumberId: internalPhoneId
        };
    },

    /**
     * Return formatted assignable external number for group
     *
     * @param {*} entry
     * @returns
     */
    _createExternalPhonenumberForGroup(entry: any) {
        return {
            assigned: false,
            countryCode: entry.nationalPrefix,
            exitCode: entry.exitCode,
            extension: entry.number,
            id: entry.id,
            intern: false,
            localAreaCode: entry.localPrefix,
            phoneNumberComplete:
                entry.exitCode +
                entry.nationalPrefix +
                entry.localPrefix +
                entry.number,
            phoneNumberForLogin:
                entry.nationalPrefix + entry.localPrefix + entry.number,
            phonenumber:
                '+' +
                entry.nationalPrefix +
                ' ' +
                entry.localPrefix +
                ' ' +
                entry.number,
            phonenumberExtension: entry.number.slice(entry.number.length - 2),
            phonenumberForForm: entry.number,
            available:
                entry.assignedGroupAccountId === null &&
                entry.assignedUserAccountId === null &&
                entry.assignedServiceId === null,
            assignedGroupAccountId: entry.assignedGroupAccountId,
            assignedUserAccountId: entry.assignedUserAccountId,
            assignedServiceId: entry.assignedServiceId,
        };
    },

    /**
     * Return formatted external number
     *
     * @param {*} entry
     * @returns
     */
    _createExternalPhonenumber(entry: any) {
        return {
            phoneNumberComplete:
                entry.exitCode +
                entry.nationalPrefix +
                entry.localPrefix +
                entry.number,
            phoneNumberForLogin:
                entry.nationalPrefix + entry.localPrefix + entry.number,
            phonenumber:
                '+' +
                entry.nationalPrefix +
                ' ' +
                entry.localPrefix +
                ' ' +
                entry.number,
            phonenumberExtension: entry.number.slice(entry.number.length - 2),
            phonenumberForForm: entry.number,
            assignedGroupAccountId: entry.assignedGroupAccountId,
            assignedUserAccountId: entry.assignedUserAccountId,
            assignedServiceId: entry.assignedServiceId,
            available:
                entry.assignedGroupAccountId === null &&
                entry.assignedUserAccountId === null &&
                entry.assignedServiceId === null,
            id: entry.id
        };
    },

    /**
     * Return formatted internal number
     *
     * @param {*} entry
     * @returns
     */
    _createInternalPhonenumber(entry: any) {
        return {
            assignedGroupAccountId: entry.assignedGroupAccountId,
            assignedUserAccountId: entry.assignedUserAccountId,
            assignedServiceId: entry.assignedServiceId,
            id: entry.id,
            number: entry.number
        };
    },

    /**
     * Return formatted assignable internal number for grou
     *
     * @param {*} entry
     * @returns
     */
    _createInternalPhonenumberForGroup(entry: any) {
        return {
            assigned: false,
            assignedGroupAccountId: entry.assignedGroupAccountId,
            assignedUserAccountId: entry.assignedUserAccountId,
            assignedServiceId: entry.assignedServiceId,
            countryCode: entry.nationalPrefix,
            exitCode: entry.exitCode,
            extension: entry.number,
            id: entry.id,
            intern: true,
            localAreaCode: entry.localPrefix
        };
    },

    /**
     * Return a the count of all PhoneNumbers and the count of all already assigend PhoneNumbers
     *
     * @returns { countOfAllPhonenumbers: Number; countOfAllAssignedPhonenumbers: Number}
     */
    getCountOfAllPhonenumbers(
        phonenumberList: any[]
    ): {
        countOfAllPhonenumbers: number;
        countOfAllAssignedPhonenumbers: number;
    } {
        let phonenumbersCountInTotal = 0;
        let phonenumbersAssignedCount = 0;
        for (const entry of phonenumberList) {
            if (this.entryPassesClientDefinedFilter(entry)) {
                phonenumbersCountInTotal++;
                if (
                    entry.assignedUserAccountId ||
                    entry.assignedGroupAccountId ||
                    entry.assignedServiceId === 11 ||
                    entry.assignedModuleInstanceId
                ) {
                    phonenumbersAssignedCount++;
                }
            }
        }
        return {
            countOfAllPhonenumbers: phonenumbersCountInTotal,
            countOfAllAssignedPhonenumbers: phonenumbersAssignedCount
        };
    },

    /**
     * Return true if provided entry passes filter
     *
     * @param {*} entry
     * @returns bollean
     */
    entryPassesClientDefinedFilter(entry: any) {
        // entry.localPrefix 3221 is excluded based on client requirement
        // entry object that contains nationalPrefix 49 and localPrefix that starts with 32 properties, is excluded based on client requirement
        // entry object that contains nationalPrefix 43 and localPrefix that starts with 72 properties, is excluded based on client requirement
        return entry.type === 'EXTERNAL' &&
        entry.localPrefix !== '3221' &&
        !(entry.nationalPrefix === '49' && entry.localPrefix.startsWith('32')) &&
        !(entry.nationalPrefix === '43' && entry.localPrefix.startsWith('72'));
    }
};
